// Tenantra Inc. - Forgot Password Page (Warm Sand Theme - Microsoft Style)
import { useState } from 'react';
import { ArrowLeft, Mail, CheckCircle, AlertCircle, Loader2 } from 'lucide-react';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import api from '@/services/api';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await api.post('/auth/forgot-password', { email });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f3f4f6] flex flex-col">
      <header className="h-16 bg-white border-b border-slate-200">
        <div className="max-w-6xl mx-auto h-full px-4 sm:px-6 flex items-center">
          <Logo size="md" data-testid="auth-logo" />
        </div>
      </header>

      <main className="flex-1 flex items-start justify-center px-4 sm:px-6 py-10 sm:py-16">
        <div className="w-full max-w-[420px]">
          <div className="bg-white border border-slate-200 rounded-lg p-6 sm:p-8 shadow-sm">
            {!sent ? (
              <>
                <div className="mb-5">
                  <h2 className="text-xl font-bold text-stone-900">Reset your password</h2>
                </div>

                {error && (
                  <Alert className="mb-4 bg-red-50 border-red-200 text-red-600 py-2">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription className="text-sm">{error}</AlertDescription>
                  </Alert>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label className="text-stone-700 text-sm">Email Address</Label>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="h-9 bg-white border-stone-200 text-stone-900 placeholder:text-stone-400 focus:border-teal-500 focus:ring-teal-500/20 text-sm"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full h-9 bg-teal-600 hover:bg-teal-700 text-white font-medium shadow-md shadow-teal-500/20 border-0 text-sm"
                    disabled={loading}
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Sending...
                      </span>
                    ) : (
                      <>
                        <Mail className="w-4 h-4 mr-2" />
                        Send Reset Link
                      </>
                    )}
                  </Button>
                </form>
              </>
            ) : (
              <div className="text-center py-4">
                <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-3">
                  <CheckCircle className="w-6 h-6 text-teal-600" />
                </div>
                <h2 className="text-xl font-bold text-stone-900 mb-2">Check Your Email</h2>
                <p className="text-sm text-stone-500 mb-4">
                  If an account exists with <strong className="text-stone-700">{email}</strong>, you'll receive a password reset link shortly.
                </p>
                <p className="text-xs text-stone-400 mb-4">
                  Didn't receive the email? Check your spam folder or try again.
                </p>
                <Button 
                  variant="outline"
                  onClick={() => { setSent(false); setEmail(''); }}
                  className="border-stone-200 text-stone-600 hover:bg-stone-50 text-sm h-9"
                >
                  Try Another Email
                </Button>
              </div>
            )}

            <div className="mt-5 pt-5 border-t border-stone-200">
              <a href="/login" className="block text-center text-sm text-teal-700 hover:text-teal-800 font-medium transition-colors">
                Back to sign in
              </a>
            </div>
          </div>
        </div>
      </main>

      <footer className="py-6 text-center text-xs text-slate-500">
        © {new Date().getFullYear()} Tenantra Inc. ·{' '}
        <a href="/privacy-policy" className="hover:text-slate-700">Privacy</a> ·{' '}
        <a href="/terms-of-service" className="hover:text-slate-700">Terms</a>
      </footer>
    </div>
  );
}
